/**
 * DocumentGenerationPage — מרכז הפקת מסמכים
 *
 * Phase 23: Document Generation Hub.
 *
 * Route: /app/inventory/documents/new
 *
 * Flow:
 *  1. Fetch all document_types for the active company.
 *  2. Show a grid of clickable document-type cards.
 *  3. When the user picks a type, slide in the appropriate form:
 *     • Customer-facing types  → SalesDocumentForm  (client + selling price)
 *     • Stock movement types   → InventoryDocumentForm (entry/exit mode)
 *  4. After successful generation show the SuccessState panel.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { InventoryDocumentPrintView } from '@/components/inventory/InventoryDocumentPrintView';
import type { InventoryPrintData } from '@/components/inventory/InventoryDocumentPrintView';
import { FileText, ArrowRight, Loader as Loader2, CircleAlert as AlertCircle, ReceiptText, ShoppingCart, Truck, ClipboardList, Package, PackagePlus, PackageMinus, RotateCcw, CreditCard, Receipt, CircleCheck as CheckCircle2, Mail, Printer, Plus, BookOpen, Boxes } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useCompany } from '@/context/CompanyContext';
import { SalesDocumentForm } from '@/pages/inventory/SalesDocumentForm';
import { InventoryDocumentForm } from '@/pages/inventory/InventoryDocumentForm';
import { ReceiptForm } from '@/pages/inventory/ReceiptForm';
import { TransferDocumentForm } from '@/pages/inventory/TransferDocumentForm';
import { fetchVatRate } from '@/pages/inventory/CompanySettingsView';
import { DocumentJournalView } from '@/components/inventory/DocumentJournalView';
import SendDocumentEmailModal from '@/components/modals/SendDocumentEmailModal';
import type { ImportableLine } from '@/components/modals/DocumentPickerModal';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DocumentType {
  id:                       string;
  name:                     string;
  prefix:                   string;   // e.g. 'INV', 'DN'
  next_number:              number;
  requires_receipt:         boolean;
  is_vat_liable:            boolean;
  updates_inventory:        boolean;
  is_system_default:        boolean;
  has_line_discount:        boolean;   // Phase 25
  first_document_issued_at: string | null;  // Phase 80
  is_payment_document:      boolean;   // Phase 96
}

// Which form to show for a given document type
type FormMode = 'receipt' | 'sales' | 'entry' | 'exit' | 'transfer' | 'generic';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Customer-facing document names — show SalesDocumentForm with client + price */
const SALES_DOC_NAMES = new Set([
  'חשבונית מס',
  'חשבונית מס קבלה',
  'חשבונית עסקה',
  'חשבונית זיכוי',
  'תעודת משלוח',
  'תעודת הזמנה',
  'קבלה',
  // Phase 79: consignment document types
  'תעודת משלוח קונסגנציה',
]);

/** Inbound stock movement names — InventoryDocumentForm entry mode */
const ENTRY_DOC_NAMES = new Set([
  'תעודת כניסה למלאי',
  'תעודת החזרה מלקוח',
]);

/** Outbound stock movement names — InventoryDocumentForm exit mode */
const EXIT_DOC_NAMES = new Set([
  'תעודת יציאה מהמלאי',
  'תעודת החזרה לספק',
]);

/** Transfer document names (consignment + standard) */
const TRANSFER_DOC_NAMES = new Set([
  'תעודת העברה בין מחסנים קונסגנציה',
  'תעודת העברה בין מחסנים',
  'תעודת החזרה מלקוח קונסגנציה',
]);

function getFormMode(dt: DocumentType): FormMode {
  if (dt.is_payment_document)          return 'receipt';
  if (SALES_DOC_NAMES.has(dt.name))    return 'sales';
  if (ENTRY_DOC_NAMES.has(dt.name))    return 'entry';
  if (EXIT_DOC_NAMES.has(dt.name))     return 'exit';
  if (TRANSFER_DOC_NAMES.has(dt.name)) return 'transfer';
  return 'generic';
}

/** Map document name → Lucide icon */
function DocIcon({ name, className = 'w-6 h-6' }: { name: string; className?: string }) {
  if (name === 'חשבונית מס')           return <ReceiptText   className={className} />;
  if (name === 'חשבונית מס קבלה')      return <Receipt        className={className} />;
  if (name === 'חשבונית עסקה')          return <ShoppingCart   className={className} />;
  if (name === 'חשבונית זיכוי')         return <CreditCard     className={className} />;
  if (name === 'קבלה')                  return <Receipt        className={className} />;
  if (name === 'תעודת הזמנה')           return <ClipboardList  className={className} />;
  if (name === 'תעודת משלוח')               return <Truck          className={className} />;
  if (name === 'תעודת משלוח קונסגנציה')    return <Boxes          className={className} />;
  if (name === 'תעודת החזרה מלקוח קונסגנציה')    return <Boxes          className={className} />;
  if (name === 'תעודת העברה בין מחסנים קונסגנציה') return <Boxes          className={className} />;
  if (name === 'תעודת החזרה מלקוח')        return <RotateCcw      className={className} />;
  if (name === 'תעודת החזרה לספק')      return <RotateCcw      className={className} />;
  if (name === 'תעודת כניסה למלאי')     return <PackagePlus    className={className} />;
  if (name === 'תעודת יציאה מהמלאי')    return <PackageMinus   className={className} />;
  return                                        <FileText        className={className} />;
}

/** Colour accent per form mode */
function modeAccent(mode: FormMode) {
  switch (mode) {
    case 'receipt': return { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'text-emerald-600', badge: 'bg-emerald-100 text-emerald-700' };
    case 'sales':   return { bg: 'bg-blue-50',    border: 'border-blue-200',    icon: 'text-blue-600',   badge: 'bg-blue-100 text-blue-700' };
    case 'entry':   return { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'text-emerald-600',badge: 'bg-emerald-100 text-emerald-700' };
    case 'exit':    return { bg: 'bg-rose-50',    border: 'border-rose-200',    icon: 'text-rose-600',   badge: 'bg-rose-100 text-rose-700' };
    default:        return { bg: 'bg-gray-50',    border: 'border-gray-200',    icon: 'text-gray-600',   badge: 'bg-gray-100 text-gray-700' };
  }
}

function modeBadgeLabel(mode: FormMode) {
  switch (mode) {
    case 'receipt': return 'קבלה / תשלום';
    case 'sales':   return 'מסמך לקוח';
    case 'entry':   return 'כניסת מלאי';
    case 'exit':    return 'יציאת מלאי';
    default:        return 'מסמך כללי';
  }
}

// ─── Success state component ──────────────────────────────────────────────────

interface SuccessStateProps {
  docTypeName:  string;
  docRef:       string;
  documentId?:  string;
  clientId?:    string;
  companyId:    string;
  onNewDoc:     () => void;
  onPrint:      () => void;
}

function SuccessState({ docTypeName, docRef, documentId, clientId, companyId, onNewDoc, onPrint }: SuccessStateProps) {
  const [showEmailModal, setShowEmailModal] = useState(false);

  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 gap-6 text-center">
      {/* Animated check circle */}
      <div className="flex items-center justify-center w-24 h-24 rounded-full bg-emerald-100 shadow-sm">
        <CheckCircle2 className="w-14 h-14 text-emerald-500" />
      </div>

      <div className="space-y-1">
        <h2 className="text-2xl font-bold text-gray-900">מסמך הופק בהצלחה!</h2>
        <p className="text-gray-500 text-sm">{docTypeName} נוצר ונשמר בהצלחה</p>
        <p className="font-mono text-2xl font-bold text-orange-600 tracking-wider mt-1">
          {docRef}
        </p>
      </div>

      {/* Action buttons */}
      <div className="flex flex-col sm:flex-row items-center gap-3 w-full max-w-md">

        {/* Print / PDF */}
        <button
          onClick={onPrint}
          className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl
                     border-2 border-blue-500 bg-blue-50 text-blue-700 text-sm font-semibold
                     hover:bg-blue-100 hover:border-blue-600 transition-colors"
        >
          <Printer className="w-4 h-4" />
          הדפס / הצג PDF
        </button>

        {/* Send email */}
        <button
          onClick={() => setShowEmailModal(true)}
          className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl
                     border-2 border-emerald-500 bg-emerald-50 text-emerald-700 text-sm font-semibold
                     hover:bg-emerald-100 hover:border-emerald-600 transition-colors"
        >
          <Mail className="w-4 h-4" />
          שלח במייל
        </button>
      </div>

      {/* Create another */}
      <button
        onClick={onNewDoc}
        className="flex items-center gap-2 px-6 py-3 rounded-xl bg-orange-500
                   hover:bg-orange-600 text-white text-sm font-semibold
                   transition-colors shadow-sm"
      >
        <Plus className="w-4 h-4" />
        הפק מסמך חדש
      </button>

      {showEmailModal && (
        <SendDocumentEmailModal
          onClose={() => setShowEmailModal(false)}
          clientId={clientId ?? null}
          companyId={companyId}
          documentId={documentId}
          docLabel={`${docTypeName} ${docRef}`}
        />
      )}
    </div>
  );
}

// ─── Main Page Component ──────────────────────────────────────────────────────

// ─── Page-level tab type (Phase 70) ──────────────────────────────────────────
// 'create'  — original document-generation hub (selector + form + success)
// 'journal' — DocumentJournalView migrated from InventoryMovementsPage
type PageTab = 'create' | 'journal';

export function DocumentGenerationPage() {
  const { activeCompanyId, activeCompany } = useCompany();
  const companyName = activeCompany?.legal_name ?? 'החברה';

  // ── Tab state (Phase 70: outer tabs — create vs journal) ──────────────────
  const [activeTab,  setActiveTab]  = useState<PageTab>('create');

  // ── Reference data ──────────────────────────────────────────────────────────
  const [docTypes,   setDocTypes]   = useState<DocumentType[]>([]);
  const [vatRate,    setVatRate]    = useState<number>(17);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState<string | null>(null);

  // ── UI state ────────────────────────────────────────────────────────────────
  const [selected,   setSelected]   = useState<DocumentType | null>(null);
  const [toast,      setToast]      = useState<{ msg: string; ok: boolean } | null>(null);

  // Success state
  const [success,    setSuccess]    = useState<{ docTypeName: string; docRef: string; documentId?: string; clientId?: string } | null>(null);
  // Print data captured from the form at submit time
  const [printData,  setPrintData]  = useState<InventoryPrintData | null>(null);
  // Lines imported from an existing document (journal → create flow)
  const [pendingImportLines, setPendingImportLines] = useState<ImportableLine[] | null>(null);
  // Ref for react-to-print
  const printRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({ contentRef: printRef });

  // ── Phase 80: First-document confirmation ──────────────────────────────────
  // Modal 1: shown when user selects a doc type for the first time (entry)
  const [firstDocEntryModal, setFirstDocEntryModal] = useState<DocumentType | null>(null);
  // Modal 2: shown before saving — user can edit prefix/next_number one last time
  const [firstDocConfirmModal, setFirstDocConfirmModal] = useState<{
    docType: DocumentType;
    pendingSubmit: () => void;
  } | null>(null);
  // Editable fields in the confirm modal
  const [confirmPrefix,     setConfirmPrefix]     = useState('');
  const [confirmNextNumber, setConfirmNextNumber] = useState('');
  const [confirmSaving,     setConfirmSaving]     = useState(false);
  const [confirmError,      setConfirmError]      = useState<string | null>(null);

  // Called when a doc card is clicked
  const handleSelectDocType = (dt: DocumentType) => {
    if (!dt.first_document_issued_at) {
      // First time — show entry warning modal
      setFirstDocEntryModal(dt);
    } else {
      setSelected(dt);
    }
  };

  // Holds the resolve function of the pending promise so modal can resolve it
  const firstDocResolveRef = useRef<((ok: boolean) => void) | null>(null);

  // Called by SalesDocumentForm / InventoryDocumentForm before first submit
  const handleBeforeFirstSubmit = useCallback((): Promise<boolean> => {
    if (!selected) return Promise.resolve(true);
    return new Promise<boolean>(resolve => {
      firstDocResolveRef.current = resolve;
      setConfirmPrefix(selected.prefix);
      setConfirmNextNumber(String(selected.next_number));
      setConfirmError(null);
      // Store the docType in the modal state (pendingSubmit unused — use ref)
      setFirstDocConfirmModal({ docType: selected, pendingSubmit: () => {} });
    });
  }, [selected]);

  const handleConfirmFirstDoc = async () => {
    if (!firstDocConfirmModal) return;
    const { docType } = firstDocConfirmModal;

    const cleanPrefix = confirmPrefix.trim().toUpperCase();
    if (!cleanPrefix || !/^[A-Z]{1,6}$/.test(cleanPrefix)) {
      setConfirmError('קידומת חייבת להכיל 1–6 אותיות לועזיות (A-Z)');
      return;
    }
    const parsedNumber = parseInt(confirmNextNumber, 10);
    if (isNaN(parsedNumber) || parsedNumber < 1 || parsedNumber > 9_999_999) {
      setConfirmError('מספר התחלתי חייב להיות בין 1 ל-9,999,999');
      return;
    }

    setConfirmSaving(true);

    // If user changed prefix or number — update via RPC first
    const prefixChanged = cleanPrefix !== docType.prefix;
    const numberChanged = parsedNumber !== docType.next_number;
    if (prefixChanged || numberChanged) {
      const { error: rpcErr } = await supabase.rpc('update_document_sequencing', {
        p_doc_type_id: docType.id,
        p_new_prefix:  cleanPrefix,
        p_new_number:  parsedNumber,
      });
      if (rpcErr) {
        setConfirmError(rpcErr.message);
        setConfirmSaving(false);
        return;
      }
      // Update local selected so the form submits with the new values
      setSelected(prev => prev ? { ...prev, prefix: cleanPrefix, next_number: parsedNumber } : prev);
      fetchData();
    }

    setConfirmSaving(false);
    setFirstDocConfirmModal(null);
    // Resolve the promise → form proceeds to submit
    firstDocResolveRef.current?.(true);
    firstDocResolveRef.current = null;
  };

  // ── Load document types + VAT ────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!activeCompanyId) return;
    setLoading(true); setError(null);
    try {
      const [dtRes, vat] = await Promise.all([
        supabase
          .from('document_types')
          .select('id, name, prefix, next_number, requires_receipt, is_vat_liable, updates_inventory, is_system_default, has_line_discount, first_document_issued_at, is_payment_document')
          .eq('company_id', activeCompanyId)
          .order('next_number'),
        fetchVatRate(activeCompanyId),
      ]);
      if (dtRes.error) throw new Error(dtRes.error.message);
      setDocTypes((dtRes.data ?? []) as DocumentType[]);
      setVatRate(vat);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'שגיאה בטעינת סוגי מסמכים');
    } finally {
      setLoading(false);
    }
  }, [activeCompanyId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Toast helper ─────────────────────────────────────────────────────────────
  const showToast = useCallback((msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // ── Success callback (passed into form) ──────────────────────────────────────
  const handleSuccess = useCallback((
    docTypeName: string,
    docRef: string,
    printDataArg: InventoryPrintData,
  ) => {
    const enriched: InventoryPrintData = {
      ...printDataArg,
      companyName: activeCompany?.legal_name ?? undefined,
    };
    setPrintData(enriched);
    setSuccess({ docTypeName, docRef, documentId: printDataArg.documentId, clientId: printDataArg.clientId });
    // Phase 80: mark first issuance (idempotent — only sets when NULL)
    if (selected) {
      supabase.rpc('mark_document_type_first_issued', { p_doc_type_id: selected.id });
    }
    fetchData();
  }, [fetchData, activeCompany?.legal_name, selected]);

  // ── Reset to selector ─────────────────────────────────────────────────────────
  const resetToSelector = () => {
    setSelected(null);
    setSuccess(null);
    setPrintData(null);
    setPendingImportLines(null);
  };

  // ── Group document types ──────────────────────────────────────────────────────
  const salesDocs   = docTypes.filter(d => d.is_payment_document || SALES_DOC_NAMES.has(d.name));
  const stockDocs   = docTypes.filter(d => !d.is_payment_document && (ENTRY_DOC_NAMES.has(d.name) || EXIT_DOC_NAMES.has(d.name)));
  const otherDocs   = docTypes.filter(d =>
    !d.is_payment_document &&
    !SALES_DOC_NAMES.has(d.name) &&
    !ENTRY_DOC_NAMES.has(d.name) &&
    !EXIT_DOC_NAMES.has(d.name)
  );

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-6" dir="rtl">

      {/* ── Page header ─────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        {activeTab === 'create' && selected && (
          <button
            onClick={resetToSelector}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors"
            title="חזרה לבחירת מסמך"
          >
            <ArrowRight className="w-5 h-5" />
          </button>
        )}
        <div className="p-2 bg-orange-100 rounded-xl">
          <FileText className="w-6 h-6 text-orange-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">הפקת מסמך חדש</h1>
          <p className="text-sm text-gray-500">
            {activeTab === 'journal'
              ? 'יומן כל המסמכים שהופקו'
              : selected
                ? `מפיק: ${selected.name}`
                : 'בחר סוג מסמך להפקה'}
          </p>
        </div>
      </div>

      {/* ── Outer tabs: יצירת מסמך / יומן מסמכים (Phase 70) ────────────────── */}
      <div className="flex items-center gap-1.5 bg-gray-100/80 rounded-2xl p-1.5 w-fit">
        <button
          onClick={() => setActiveTab('create')}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-medium rounded-xl
                      transition-all duration-150 whitespace-nowrap
                      ${activeTab === 'create'
                        ? 'bg-white text-orange-700 shadow-sm border border-orange-100'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-white/60'}`}
        >
          <FileText className="w-4 h-4" />
          יצירת מסמך
        </button>
        <button
          onClick={() => setActiveTab('journal')}
          className={`flex items-center gap-2 px-5 py-3 text-sm font-medium rounded-xl
                      transition-all duration-150 whitespace-nowrap
                      ${activeTab === 'journal'
                        ? 'bg-white text-orange-700 shadow-sm border border-orange-100'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-white/60'}`}
        >
          <BookOpen className="w-4 h-4" />
          יומן מסמכים
        </button>
      </div>

      {/* ── Journal tab (Phase 70: migrated from InventoryMovementsPage) ────── */}
      {activeTab === 'journal' && activeCompanyId && (
        <DocumentJournalView
          companyId={activeCompanyId}
          companyName={companyName}
          onCreateFromDocument={async (docId) => {
            const { data } = await supabase
              .from('inventory_document_lines')
              .select('product_id, quantity, unit_price, discount_pct, line_total, inventory_products ( name )')
              .eq('document_id', docId)
              .order('line_no');
            if (data?.length) {
              setPendingImportLines(data.map((l: any) => ({
                product_id:   l.product_id,
                product_name: l.inventory_products?.name ?? '',
                quantity:     l.quantity ?? 0,
                unit_price:   l.unit_price ?? 0,
                discount_pct: l.discount_pct ?? 0,
                line_total:   l.line_total ?? 0,
              })));
              setSelected(null);
              setSuccess(null);
              setActiveTab('create');
              showToast('בחר סוג מסמך — השורות יוטענו אוטומטית', true);
            }
          }}
        />
      )}
      {activeTab === 'journal' && !activeCompanyId && (
        <div className="flex items-center justify-center py-20 gap-3 text-gray-400">
          <ClipboardList className="w-5 h-5" />
          <span className="text-sm">אין חברה פעילה</span>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          BELOW THIS LINE: 'create' tab content only
         ══════════════════════════════════════════════════════════════════════ */}

      {/* ── Toast ───────────────────────────────────────────────────────────── */}
      {activeTab === 'create' && toast && (
        <div className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm
          ${toast.ok
            ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
            : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {toast.ok
            ? <CheckCircle2 className="w-4 h-4 shrink-0" />
            : <AlertCircle  className="w-4 h-4 shrink-0" />}
          {toast.msg}
        </div>
      )}

      {/* ── Error ───────────────────────────────────────────────────────────── */}
      {activeTab === 'create' && error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200
                        rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" />{error}
        </div>
      )}

      {/* ── Loading ─────────────────────────────────────────────────────────── */}
      {activeTab === 'create' && loading && (
        <div className="flex items-center justify-center py-24 gap-3 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="text-sm">טוען סוגי מסמכים…</span>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          STATE A: No document selected — show selector grid
         ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'create' && !loading && !selected && (
        <div className="space-y-8">

          {/* ── Customer Documents ─── */}
          {salesDocs.length > 0 && (
            <section>
              <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                <ReceiptText className="w-3.5 h-3.5" />
                מסמכי לקוח
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {salesDocs.map(dt => (
                  <DocCard key={dt.id} dt={dt} onSelect={handleSelectDocType} />
                ))}
              </div>
            </section>
          )}

          {/* ── Stock Movement Documents ─── */}
          {stockDocs.length > 0 && (
            <section>
              <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                <Package className="w-3.5 h-3.5" />
                תנועות מלאי
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {stockDocs.map(dt => (
                  <DocCard key={dt.id} dt={dt} onSelect={handleSelectDocType} />
                ))}
              </div>
            </section>
          )}

          {/* ── Other / Custom Documents ─── */}
          {otherDocs.length > 0 && (
            <section>
              <h2 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                <FileText className="w-3.5 h-3.5" />
                מסמכים נוספים
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {otherDocs.map(dt => (
                  <DocCard key={dt.id} dt={dt} onSelect={handleSelectDocType} />
                ))}
              </div>
            </section>
          )}

          {!loading && docTypes.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-400">
              <FileText className="w-10 h-10 opacity-30" />
              <p className="text-sm">לא נמצאו סוגי מסמכים — הרץ את ה-SQL של Phase 17 ו-18</p>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          STATE B: Document selected — show the appropriate form or success
         ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'create' && !loading && selected && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

          {/* Hidden print view — mounted when printData is available so react-to-print can access it */}
          {printData && (
            <div style={{ display: 'none' }}>
              <InventoryDocumentPrintView ref={printRef} data={printData} />
            </div>
          )}

          {/* Success state */}
          {success ? (
            <SuccessState
              docTypeName={success.docTypeName}
              docRef={success.docRef}
              documentId={success.documentId}
              clientId={success.clientId}
              companyId={activeCompanyId!}
              onNewDoc={resetToSelector}
              onPrint={() => handlePrint()}
            />
          ) : (
            <>
              {/* Receipt / payment form (Phase 96) */}
              {getFormMode(selected) === 'receipt' && activeCompanyId && (
                <ReceiptForm
                  docType={selected}
                  companyId={activeCompanyId}
                  vatRate={vatRate}
                  showToast={showToast}
                  onSuccess={handleSuccess}
                  onBeforeFirstSubmit={!selected.first_document_issued_at ? handleBeforeFirstSubmit : undefined}
                />
              )}

              {/* Sales / customer-facing form */}
              {getFormMode(selected) === 'sales' && activeCompanyId && (
                <SalesDocumentForm
                  docType={selected}
                  companyId={activeCompanyId}
                  vatRate={vatRate}
                  showToast={showToast}
                  onSuccess={handleSuccess}
                  onBeforeFirstSubmit={!selected.first_document_issued_at ? handleBeforeFirstSubmit : undefined}
                  initialLines={pendingImportLines ?? undefined}
                />
              )}

              {/* Inventory entry form */}
              {getFormMode(selected) === 'entry' && activeCompanyId && (
                <InventoryDocumentForm
                  mode="entry"
                  docTypeOverride={selected}
                  companyId={activeCompanyId}
                  vatRate={vatRate}
                  showToast={showToast}
                  onSuccess={handleSuccess}
                  onBeforeFirstSubmit={!selected.first_document_issued_at ? handleBeforeFirstSubmit : undefined}
                  initialLines={pendingImportLines ?? undefined}
                />
              )}

              {/* Inventory exit form */}
              {getFormMode(selected) === 'exit' && activeCompanyId && (
                <InventoryDocumentForm
                  mode="exit"
                  docTypeOverride={selected}
                  companyId={activeCompanyId}
                  vatRate={vatRate}
                  showToast={showToast}
                  onSuccess={handleSuccess}
                  onBeforeFirstSubmit={!selected.first_document_issued_at ? handleBeforeFirstSubmit : undefined}
                  initialLines={pendingImportLines ?? undefined}
                />
              )}

              {/* Consignment transfer form (WHTCO) */}
              {getFormMode(selected) === 'transfer' && activeCompanyId && (
                <TransferDocumentForm
                  docType={selected}
                  companyId={activeCompanyId}
                  vatRate={vatRate}
                  showToast={showToast}
                  onSuccess={handleSuccess}
                  onBeforeFirstSubmit={!selected.first_document_issued_at ? handleBeforeFirstSubmit : undefined}
                  initialLines={pendingImportLines ?? undefined}
                />
              )}

              {/* Generic fallback */}
              {getFormMode(selected) === 'generic' && (
                <div className="flex flex-col items-center justify-center py-20 gap-3 text-gray-400">
                  <FileText className="w-10 h-10 opacity-30" />
                  <p className="text-sm">טופס למסמך זה יהיה זמין בקרוב</p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          Phase 80 — Modal 1: First-time entry warning
         ══════════════════════════════════════════════════════════════════════ */}
      {firstDocEntryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
             style={{ backgroundColor: 'rgba(0,0,0,0.45)' }} dir="rtl">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">

            <div className="flex items-center gap-3 px-6 py-5 border-b border-gray-100">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-base">מסמך ראשון מסוג זה</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {firstDocEntryModal.name}
                </p>
              </div>
            </div>

            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-gray-700 leading-relaxed">
                זה יהיה המסמך הראשון מסוג{' '}
                <strong className="text-gray-900">{firstDocEntryModal.name}</strong>.
                לאחר הפקת המסמך הראשון, הקידומת והמספור לא יהיו ניתנים לשינוי.
              </p>

              <div className="flex items-center gap-3 px-4 py-3 bg-orange-50 border border-orange-200 rounded-xl">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">קידומת:</span>
                    <span className="font-mono font-bold text-gray-800 text-sm bg-white px-2 py-0.5 rounded border border-gray-200">
                      {firstDocEntryModal.prefix}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">מספר ראשון:</span>
                    <span className="font-mono font-bold text-gray-800 text-sm bg-white px-2 py-0.5 rounded border border-gray-200">
                      {firstDocEntryModal.prefix}{firstDocEntryModal.next_number}
                    </span>
                  </div>
                </div>
              </div>

              <p className="text-xs text-gray-500 leading-relaxed">
                בשלב הבא תוכל לאשר או לשנות את הקידומת והמספור לפני הגשת המסמך הראשון.
              </p>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button
                onClick={() => setFirstDocEntryModal(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800
                           border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                ביטול
              </button>
              <button
                onClick={() => {
                  const dt = firstDocEntryModal;
                  setFirstDocEntryModal(null);
                  setSelected(dt);
                }}
                className="flex items-center gap-2 px-5 py-2 bg-orange-600 hover:bg-orange-700
                           text-white text-sm font-semibold rounded-lg transition-colors"
              >
                המשך להפקה
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          Phase 80 — Modal 2: Confirm prefix/number before first submission
         ══════════════════════════════════════════════════════════════════════ */}
      {firstDocConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
             style={{ backgroundColor: 'rgba(0,0,0,0.45)' }} dir="rtl">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">

            <div className="flex items-center gap-3 px-6 py-5 border-b border-gray-100">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <FileText className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-base">אישור לפני הפקת מסמך ראשון</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {firstDocConfirmModal.docType.name}
                </p>
              </div>
            </div>

            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-gray-700 leading-relaxed">
                זהו המסמך הראשון מסוג{' '}
                <strong className="text-gray-900">{firstDocConfirmModal.docType.name}</strong>.
                לאחר ההפקה, הקידומת והמספור יינעלו. אנא אשר או ערוך לפני ההמשך.
              </p>

              {confirmError && (
                <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border
                                border-red-200 rounded-lg text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {confirmError}
                </div>
              )}

              {/* Prefix field */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">קידומת מסמך</label>
                <input
                  type="text"
                  value={confirmPrefix}
                  onChange={e => { setConfirmPrefix(e.target.value.toUpperCase()); setConfirmError(null); }}
                  maxLength={6}
                  dir="ltr"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm font-mono
                             focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent
                             uppercase"
                  placeholder="e.g. INV"
                />
                <p className="text-xs text-gray-400">1–6 אותיות לועזיות, ייחודית לחברה</p>
              </div>

              {/* Starting number field */}
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700">מספור התחלתי</label>
                <input
                  type="number"
                  min={1}
                  max={9999999}
                  value={confirmNextNumber}
                  onChange={e => { setConfirmNextNumber(e.target.value); setConfirmError(null); }}
                  dir="ltr"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm font-mono
                             focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent"
                  placeholder="1001"
                />
                <p className="text-xs text-gray-400">המסמך הראשון יהיה: {confirmPrefix || '...'}{confirmNextNumber || '...'}</p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button
                onClick={() => {
                  setFirstDocConfirmModal(null);
                  firstDocResolveRef.current?.(false);
                  firstDocResolveRef.current = null;
                }}
                disabled={confirmSaving}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800
                           border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                ביטול
              </button>
              <button
                onClick={handleConfirmFirstDoc}
                disabled={confirmSaving}
                className="flex items-center gap-2 px-5 py-2 bg-amber-600 hover:bg-amber-700
                           disabled:opacity-50 disabled:cursor-not-allowed
                           text-white text-sm font-semibold rounded-lg transition-colors"
              >
                {confirmSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                אשר והפק
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ─── DocCard sub-component ────────────────────────────────────────────────────

function DocCard({
  dt,
  onSelect,
}: {
  dt: DocumentType;
  onSelect: (dt: DocumentType) => void;
}) {
  const mode   = getFormMode(dt);
  const accent = modeAccent(mode);

  return (
    <button
      onClick={() => onSelect(dt)}
      className={`group flex flex-col items-start gap-3 p-4 rounded-2xl border-2
                  ${accent.border} ${accent.bg}
                  hover:shadow-md hover:scale-[1.02] active:scale-[0.99]
                  transition-all duration-150 text-right w-full`}
    >
      {/* Icon */}
      <div className={`p-2.5 rounded-xl bg-white/70 shadow-sm ${accent.icon}`}>
        <DocIcon name={dt.name} className="w-5 h-5" />
      </div>

      {/* Name */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-gray-800 leading-snug">{dt.name}</p>
        <p className={`mt-1 text-xs font-medium ${accent.icon}`}>
          {modeBadgeLabel(mode)}
        </p>
      </div>

      {/* Document reference badge: PREFIX + number */}
      <div className="self-end px-2 py-0.5 rounded-md bg-white/70 border border-gray-200
                       text-xs font-mono font-bold text-gray-700">
        {(dt.prefix || '') + dt.next_number}
      </div>
    </button>
  );
}
